import { API_ENDPOINTS, apiService } from "../api";
import { isMockApiFlag } from "../apiMode";
import {
  buildMockInviteeResults,
  MOCK_STORY_INVITEES,
  MOCK_STORY_INVITEES_LATENCY_MS,
  type StoryInviteesScenario,
} from "./storyInviteesMock";
import type {
  CreateInviteesRequest,
  CreateInviteesResponse,
  StoryInviteeRead,
} from "./storyInvitees.types";

// Toggle: REACT_APP_STORY_INVITEES_API=mock → offline fixtures
const isMockStoryInviteesApi = (): boolean =>
  isMockApiFlag(process.env.REACT_APP_STORY_INVITEES_API);

const mockScenario = (): StoryInviteesScenario =>
  (process.env.REACT_APP_STORY_INVITEES_SCENARIO as StoryInviteesScenario) ??
  "default";

const settle = <T>(value: T): Promise<T> =>
  new Promise((resolve) =>
    setTimeout(() => resolve(value), MOCK_STORY_INVITEES_LATENCY_MS),
  );

/** Mirrors the interceptor's error shape so `status` branches behave as in live mode. */
const failLikeBackend = (status: number, message: string): Promise<never> =>
  new Promise((_resolve, reject) =>
    setTimeout(
      () => reject(Object.assign(new Error(message), { status })),
      MOCK_STORY_INVITEES_LATENCY_MS,
    ),
  );

/** Lists the full invite lifecycle (pending/accepted/expired/revoked) for a story. */
export const fetchStoryInvitees = (
  storyId: string,
): Promise<StoryInviteeRead[]> => {
  if (isMockStoryInviteesApi()) {
    const scenario = mockScenario();
    if (scenario === "error") {
      return failLikeBackend(500, "Invitee service is unavailable (mock).");
    }
    return settle(scenario === "empty" ? [] : MOCK_STORY_INVITEES);
  }
  return apiService.get<StoryInviteeRead[]>(
    API_ENDPOINTS.stories.invitees(storyId),
  );
};

/**
 * Invites one or more people to a story in a single batch call. Per-item
 * resolution — one bad entry doesn't fail the others; each entry gets its
 * own outcome in `results`, same order as the request.
 */
export const createStoryInvitees = (
  storyId: string,
  request: CreateInviteesRequest,
): Promise<CreateInviteesResponse> => {
  if (isMockStoryInviteesApi()) {
    if (mockScenario() === "error") {
      return failLikeBackend(500, "Invitee service is unavailable (mock).");
    }
    return settle({ results: buildMockInviteeResults(request.invitees) });
  }
  return apiService.post<CreateInviteesResponse>(
    API_ENDPOINTS.stories.invitees(storyId),
    request,
  );
};
